(function () {
  "use strict";

  var config = window.KionVslTrackingConfig || {};
  var STORAGE_KEY = "kion_vsl_attribution_v1";
  var SCHEMA_VERSION = 1;
  var MAX_VALUE_LENGTH = 200;
  var MAX_CLICK_ID_LENGTH = 500;
  var CAMPAIGN_FIELDS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_id",
    "meta_campaign_id",
    "meta_adset_id",
    "meta_ad_id",
    "fbclid"
  ];
  var initializationPromise;
  var cartSyncPromise;
  var currentState = null;
  var engagementInitialized = false;

  function sanitizeValue(value, maxLength) {
    if (typeof value !== "string") return "";

    return value
      .replace(/[\u0000-\u001F\u007F]/g, "")
      .trim()
      .slice(0, maxLength || MAX_VALUE_LENGTH);
  }

  function safeStorage(storage) {
    try {
      var testKey = "__kion_vsl_storage_test__";
      storage.setItem(testKey, "1");
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return null;
    }
  }

  function readState() {
    var storage = safeStorage(window.localStorage);
    if (!storage) return null;

    try {
      var parsed = JSON.parse(storage.getItem(STORAGE_KEY));
      if (!parsed || parsed.schema_version !== SCHEMA_VERSION || parsed.funnel_id !== "vsl") return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeState(state) {
    var storage = safeStorage(window.localStorage);
    if (!storage) return;

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Storage can be unavailable or full. The in-memory state still supports this page view.
    }
  }

  function getCampaignValues() {
    var values = {};
    var params = new URLSearchParams(window.location.search || "");

    CAMPAIGN_FIELDS.forEach(function (field) {
      if (!params.has(field)) return;

      var maxLength = field === "fbclid" ? MAX_CLICK_ID_LENGTH : MAX_VALUE_LENGTH;
      var value = sanitizeValue(params.get(field), maxLength);
      if (value) values[field] = value;
    });

    return values;
  }

  function hasCampaignValues(values) {
    return CAMPAIGN_FIELDS.some(function (field) {
      return Boolean(values[field]);
    });
  }

  function createTouch(campaignValues, vslVersion) {
    var touch = {
      landing_page: sanitizeValue(window.location.pathname, 300),
      timestamp: new Date().toISOString()
    };

    if (vslVersion) touch.vsl_version = sanitizeValue(vslVersion, 100);

    CAMPAIGN_FIELDS.forEach(function (field) {
      if (campaignValues[field]) touch[field] = campaignValues[field];
    });

    return touch;
  }

  function updateAttributionState() {
    var state = readState();
    var campaignValues = getCampaignValues();
    var isEntry = config.isVslEntry === true && Boolean(config.vslVersion);

    if (!state && !isEntry) return null;

    if (!state) {
      var firstTouch = createTouch(campaignValues, config.vslVersion);
      state = {
        schema_version: SCHEMA_VERSION,
        funnel_id: "vsl",
        vsl_version: sanitizeValue(config.vslVersion, 100),
        last_vsl_version: sanitizeValue(config.vslVersion, 100),
        first_touch: firstTouch,
        last_non_direct_touch: hasCampaignValues(campaignValues) ? firstTouch : null
      };
    } else if (isEntry) {
      state.last_vsl_version = sanitizeValue(config.vslVersion, 100);
      if (!state.vsl_version) state.vsl_version = state.last_vsl_version;
    }

    if (hasCampaignValues(campaignValues)) {
      state.last_non_direct_touch = createTouch(
        campaignValues,
        isEntry ? config.vslVersion : state.last_vsl_version || state.vsl_version
      );
    }

    writeState(state);
    return state;
  }

  function setAttribute(attributes, key, value) {
    var sanitized = sanitizeValue(String(value || ""), key === "fbclid" || /_fbclid$/.test(key) ? MAX_CLICK_ID_LENGTH : 300);
    if (sanitized) attributes[key] = sanitized;
  }

  function buildCartAttributes(state) {
    if (!state || state.funnel_id !== "vsl") return {};

    var attributes = {};
    var first = state.first_touch || {};
    var last = state.last_non_direct_touch || {};
    var active = Object.keys(last).length ? last : first;

    setAttribute(attributes, "funnel_id", state.funnel_id);
    setAttribute(attributes, "vsl_version", state.vsl_version);
    setAttribute(attributes, "last_vsl_version", state.last_vsl_version);
    setAttribute(attributes, "first_landing_page", first.landing_page);
    setAttribute(attributes, "first_touch_timestamp", first.timestamp);
    setAttribute(attributes, "last_non_direct_landing_page", last.landing_page);
    setAttribute(attributes, "last_non_direct_touch_timestamp", last.timestamp);

    CAMPAIGN_FIELDS.forEach(function (field) {
      setAttribute(attributes, field, active[field]);
      setAttribute(attributes, "first_" + field, first[field]);
      setAttribute(attributes, "last_" + field, last[field]);
    });

    return attributes;
  }

  function customerPrivacyAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(
        privacy &&
        privacy.analyticsProcessingAllowed() &&
        privacy.marketingAllowed()
      );
    } catch (error) {
      return false;
    }
  }

  function loadCustomerPrivacy() {
    return new Promise(function (resolve) {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        resolve();
        return;
      }

      if (!window.Shopify || typeof window.Shopify.loadFeatures !== "function") {
        resolve();
        return;
      }

      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        function () {
          resolve();
        }
      );
    });
  }

  function cartUpdateUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return root + "cart/update.js";
  }

  function syncCartAttributes() {
    if (!customerPrivacyAllowed()) return Promise.resolve(null);

    var state = currentState || readState();
    var attributes = buildCartAttributes(state);
    if (!Object.keys(attributes).length) return Promise.resolve(null);
    if (cartSyncPromise) return cartSyncPromise;

    cartSyncPromise = window.fetch(cartUpdateUrl(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Unable to save VSL cart attribution");
        return response.json();
      })
      .catch(function (error) {
        if (window.console && typeof window.console.warn === "function") {
          window.console.warn("Kion VSL attribution was not written to the cart.", error);
        }
        return null;
      })
      .then(function (result) {
        cartSyncPromise = null;
        return result;
      });

    return cartSyncPromise;
  }

  function sessionEventKey(eventName, metadata) {
    return [
      "kion_vsl_event",
      sanitizeValue(config.pagePath || window.location.pathname, 300),
      sanitizeValue(config.vslVersion || "", 100),
      sanitizeValue(metadata.video_id || "no-video", 100),
      eventName
    ].join(":");
  }

  function eventAlreadyFired(eventName, metadata) {
    var storage = safeStorage(window.sessionStorage);
    if (!storage) return false;

    var key = sessionEventKey(eventName, metadata);
    if (storage.getItem(key) === "1") return true;
    storage.setItem(key, "1");
    return false;
  }

  function publishEvent(eventName, metadata) {
    if (!customerPrivacyAllowed() || !config.isVslEntry) return;

    var payload = {
      event: eventName,
      funnel_id: "vsl",
      vsl_version: sanitizeValue(config.vslVersion, 100),
      video_id: sanitizeValue(metadata.video_id || "", 100),
      page_path: sanitizeValue(config.pagePath || window.location.pathname, 300)
    };

    if (typeof metadata.progress === "number") payload.progress = metadata.progress;
    if (eventAlreadyFired(eventName, payload)) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      document.dispatchEvent(new CustomEvent("kion:vsl_event", { detail: payload }));
    } catch (error) {
      // CustomEvent is diagnostic only; dataLayer remains the source of truth.
    }
  }

  function wistiaVideoIds() {
    var ids = [];
    var frames = document.querySelectorAll('iframe[src*="fast.wistia.net/embed/iframe/"]');

    Array.prototype.forEach.call(frames, function (frame) {
      var match = frame.src.match(/\/embed\/iframe\/([a-zA-Z0-9_-]+)/);
      if (match && ids.indexOf(match[1]) === -1) ids.push(match[1]);
    });

    return ids;
  }

  function bindWistiaVideo(videoId) {
    window._wq = window._wq || [];
    window._wq.push({
      id: videoId,
      onReady: function (video) {
        video.bind("play", function () {
          publishEvent("vsl_started", { video_id: videoId, progress: 0 });
        });

        video.bind("secondchange", function () {
          var watched = Number(video.percentWatched ? video.percentWatched() : 0) * 100;
          [25, 50, 75].forEach(function (milestone) {
            if (watched >= milestone) {
              publishEvent("vsl_progress_" + milestone, {
                video_id: videoId,
                progress: milestone
              });
            }
          });
        });

        video.bind("end", function () {
          publishEvent("vsl_completed", { video_id: videoId, progress: 100 });
        });
      }
    });
  }

  function initializeEngagementTracking() {
    if (!config.isVslEntry || engagementInitialized) return;
    engagementInitialized = true;

    var videoIds = wistiaVideoIds();
    var primaryVideoId = videoIds[0] || "";
    publishEvent("vsl_viewed", { video_id: primaryVideoId, progress: 0 });
    videoIds.forEach(bindWistiaVideo);

    document.addEventListener(
      "click",
      function (event) {
        var link = event.target && event.target.closest && event.target.closest('a[href*="/pages/vsloffer"]');
        if (!link) return;
        publishEvent("vsl_cta_clicked", { video_id: primaryVideoId });
      },
      true
    );
  }

  function initialize() {
    if (!customerPrivacyAllowed()) return null;

    currentState = updateAttributionState();
    if (!currentState) return null;

    syncCartAttributes();
    initializeEngagementTracking();
    return currentState;
  }

  initializationPromise = loadCustomerPrivacy().then(initialize);

  document.addEventListener("visitorConsentCollected", function () {
    if (!customerPrivacyAllowed()) return;
    initializationPromise = Promise.resolve(initialize());
  });

  window.KionVslAttribution = {
    getState: function () {
      return currentState || readState();
    },
    ready: function () {
      return initializationPromise;
    },
    syncCartAttributes: function () {
      return initializationPromise.then(syncCartAttributes);
    },
    prepareCheckout: function () {
      return initializationPromise.then(syncCartAttributes);
    }
  };
})();
